import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useCreateAttendanceConceptPanel } from '@/hooks/attendance/concept/useCreateAttendanceConceptPanel';


interface ConceptCreatePanelProps {
  onSaved?: () => void;
}

export const ConceptCreatePanel: React.FC<ConceptCreatePanelProps> = ({ onSaved }) => {
  const { t: translate } = useTranslation();

  const {
    nameValue,
    codeValue,
    handlingValue,
    calculationFormulaValue,
    obtentionFormulaValue,
    handlingOptions,
    formulaOptions,
    isSubmitting,
    isReady,
    isReadOnly,
    errors,
    isSubmitted,
    handleNameChange,
    handleCodeChange,
    handleHandlingChange,
    handleCalculationFormulaChange,
    handleObtentionFormulaChange,
    handleSubmit,
  } = useCreateAttendanceConceptPanel({ onSaved });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="concept-create-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:attendanceConcept.fields.name')}
          placeholder={translate('attendance:attendanceConcept.placeholders.name')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="concept-create-name"
        />

        <LabelInput
          label={translate('attendance:attendanceConcept.fields.codeName')}
          placeholder={translate('attendance:attendanceConcept.placeholders.code')}
          value={codeValue}
          onChange={handleCodeChange}
          required
          disabled={isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="concept-create-code"
        />

        <Selector
          label={translate('attendance:attendanceConcept.fields.handling')}
          placeholder={translate('attendance:attendanceConcept.placeholders.handling')}
          options={handlingOptions}
          value={handlingValue || undefined}
          onChange={handleHandlingChange}
          disabled={isSubmitting}
          color={errors.handlingId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.handlingId?.message && isSubmitted
              ? translate(String(errors.handlingId.message))
              : undefined
          }
          required
          data-testid="concept-create-handling"
        />

        <Selector
          label={translate('attendance:attendanceConcept.fields.calculationFormula')}
          placeholder={translate('attendance:attendanceConcept.placeholders.calculationFormula')}
          options={formulaOptions}
          value={calculationFormulaValue || undefined}
          onChange={handleCalculationFormulaChange}
          disabled={isSubmitting}
          color={errors.calculationFormulaId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.calculationFormulaId?.message && isSubmitted
              ? translate(String(errors.calculationFormulaId.message))
              : undefined
          }
          required
          data-testid="concept-create-calculation-formula"
        />

        <Selector
          label={translate('attendance:attendanceConcept.fields.obtentionFormula')}
          placeholder={translate('attendance:attendanceConcept.placeholders.obtentionFormula')}
          options={formulaOptions}
          value={obtentionFormulaValue || undefined}
          onChange={handleObtentionFormulaChange}
          disabled={isSubmitting}
          color={errors.obtentionFormulaId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.obtentionFormulaId?.message && isSubmitted
              ? translate(String(errors.obtentionFormulaId.message))
              : undefined
          }
          required
          data-testid="concept-create-obtention-formula"
        />
      </SidePanelFormLayout.Fields>

      {!isReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="create">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default ConceptCreatePanel;
